import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  keyframes,
} from "@mui/material";
import {
  Home,
  Info,
  Business,
  Sync,
  Assessment,
  ContactMail,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';

// Gentle pulse animation for icons
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

export default function Sitemap() {
  const pages = [
    {
      title: "Home",
      description: "Welcome to Namami India Support Services",
      path: "/",
      icon: <Home fontSize="large" />,
      color: "#1976d2",
    },
    {
      title: "About Us",
      description: "Learn about our company and mission",
      path: "/about",
      icon: <Info fontSize="large" />,
      color: "#2196f3",
    },
    {
      title: "Services",
      description: "Explore our comprehensive facility management services",
      path: "/services",
      icon: <Business fontSize="large" />,
      color: "#42a5f5",
    },
    {
      title: "Transition",
      description: "Our service transition process",
      path: "/transition",
      icon: <Sync fontSize="large" />,
      color: "#64b5f6",
    },
    {
      title: "Reports",
      description: "Service reports and documentation",
      path: "/reports",
      icon: <Assessment fontSize="large" />,
      color: "#90caf9",
    },
    {
      title: "Contact",
      description: "Get in touch with our team",
      path: "/contact",
      icon: <ContactMail fontSize="large" />,
      color: "#0d47a1",
    },
  ];

  const legalPages = [
    { title: "Privacy Policy", path: "/privacy" },
    { title: "Terms of Service", path: "/terms" },
    { title: "Sitemap", path: "/sitemap" },
  ];

  return (
    <><Helmet>
  <title>Sitemap - Namami India Support Services</title>
  <meta
    name="description"
    content="Navigate the Namami India website easily. View all links to pages, services, and information in one organized sitemap."
  />
  <meta
    name="keywords"
    content="Namami India, sitemap, page links, navigation, website structure"
  />
  <meta property="og:title" content="Sitemap - Namami India" />
  <meta
    property="og:description"
    content="Find your way around Namami India's website with our easy-to-navigate sitemap."
  />
  <meta property="og:url" content="https://www.namamiindia.co.in/sitemap" />
  <link rel="canonical" href="https://www.namamiindia.co.in/sitemap" />
</Helmet>

    <Box
      sx={{
        py: 10,
        background: "linear-gradient(180deg, #ffffff, #f7f9fc)",
        color: "#1a1a1a",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="lg" sx={{ textAlign: "center" }}>
        {/* === Header === */}
        <Typography
          variant="h3"
          fontWeight={800}
          mb={2}
          sx={{
            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Sitemap
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          mb={6}
        >
          Explore all pages of Namami India with ease and clarity
        </Typography>

        {/* === Page Cards === */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 32,
            columnGap: 32,
            rowGap: 40,
          }}
        >
          {pages.map((page, i) => (
            <Card
              key={i}
              sx={{
                width: { xs: "90%", sm: "45%", md: "30%" },
                backgroundColor: "#ffffff",
                borderRadius: 4,
                border: "1px solid #e0e0e0",
                boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
                transition: "transform 0.4s ease, box-shadow 0.4s ease",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: `0 10px 30px ${page.color}33`,
                },
              }}
            >
              <CardContent sx={{ textAlign: "center", p: 4 }}>
                <Box
                  sx={{
                    width: 65,
                    height: 65,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2.5,
                    background: `${page.color}11`,
                    color: page.color,
                    animation: `${pulse} 3s infinite ease-in-out`,
                  }}
                >
                  {page.icon}
                </Box>

                <Typography variant="h5" fontWeight={600} mb={1}>
                  {page.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={3}
                >
                  {page.description}
                </Typography>
                <Button
                  component={Link}
                  to={page.path}
                  variant="outlined"
                  sx={{
                    borderColor: page.color,
                    color: page.color,
                    borderRadius: 3,
                    px: 3,
                    textTransform: "none",
                    "&:hover": {
                      background: page.color,
                      color: "#fff",
                      borderColor: page.color,
                    },
                  }}
                >
                  Visit Page
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* === Legal Pages === */}
        <Box sx={{ mt: 10 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            mb={3}
            sx={{
              color: "#1976d2",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Legal Pages
          </Typography>
          <Stack
            direction="row"
            justifyContent="center"
            flexWrap="wrap"
            gap={2}
            mt={2}
          >
            {legalPages.map((page, i) => (
              <Button
                key={i}
                component={Link}
                to={page.path}
                variant="text"
                sx={{
                  color: "#424242",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    color: "#1976d2",
                    transform: "translateY(-2px)",
                    transition: "0.3s",
                  },
                }}
              >
                {page.title}
              </Button>
            ))}
          </Stack>
        </Box>

        {/* === CTA Section === */}
        <Box sx={{ textAlign: "center", mt: 10 }}>
          <Typography variant="h6" mb={2} color="text.secondary">
            Can’t find what you’re looking for?
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{
              px: 5,
              py: 1.3,
              fontWeight: 600,
              background: "linear-gradient(90deg, #1976d2, #42a5f5)",
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(33,150,243,0.3)",
              "&:hover": {
                background: "linear-gradient(90deg, #1565c0, #0d47a1)",
              },
            }}
          >
            Contact Our Team
          </Button>
        </Box>
      </Container>
    </Box>
    </>
  );
}
